import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Eye, User, Footprints, ListChecks, Pencil, Printer } from "lucide-react";
import { toast } from "sonner";
import { api, viewerApi, errMsg } from "@/lib/api";
import { usePlayback } from "@/lib/usePlayback";
import { totalDuration, playerById, playerWindow } from "@/lib/playEngine";
import { PlayCanvas } from "@/components/field/PlayCanvas";
import { PlaybackBar } from "@/components/field/PlaybackBar";
import { Legend } from "@/components/field/Legend";
import { PositionPicker, JobCard } from "@/components/learn/JobCard";
import { WalkThrough } from "@/components/learn/WalkThrough";
import { QuizRunner } from "@/components/learn/QuizRunner";
import { ViewerHeader } from "@/pages/ViewerPlaybook";
import { catLabel } from "@/components/PlayFilters";

function useLearnData(playId, viewer) {
  const [state, setState] = useState({ play: null, team: null, err: "" });
  useEffect(() => {
    (async () => {
      try {
        if (viewer) {
          const [p, t] = await Promise.all([viewerApi.get(`/viewer/plays/${playId}`), viewerApi.get("/viewer/team")]);
          setState({ play: p.data, team: t.data, err: "" });
        } else {
          const p = await api.get(`/plays/${playId}`);
          const t = await api.get(`/teams/${p.data.team_id}`);
          setState({ play: p.data, team: t.data, err: "" });
        }
      } catch (e) { setState({ play: null, team: null, err: errMsg(e) }); }
    })();
  }, [playId, viewer]);
  return state;
}

function LearnBody({ play, team, viewer }) {
  const pb = usePlayback(totalDuration(play));
  const [mode, setMode] = useState("watch");
  const [posId, setPosId] = useState(null);
  const [trails, setTrails] = useState(true);
  const [full, setFullRaw] = useState(false);
  const player = posId && playerById(play, posId);

  const setFull = (v) => {
    setFullRaw(v);
    try { if (v) document.documentElement.requestFullscreen?.(); else if (document.fullscreenElement) document.exitFullscreen?.(); } catch (_) { /* pseudo full-screen still works */ }
  };
  useEffect(() => { const h = () => !document.fullscreenElement && setFullRaw(false); document.addEventListener("fullscreenchange", h); return () => document.removeEventListener("fullscreenchange", h); }, []);

  const replay = () => { if (!posId) return toast("Pick your position first"); setMode("job"); const w = playerWindow(play, posId); pb.playRange(w.start, Math.min(pb.duration, w.end)); };
  const go = (m) => { setMode(m); pb.seek(0); if (m === "watch") pb.restart(); if (m === "job" && !posId) toast("Pick your position below"); };
  const MODES = [["watch", "Watch Whole Play", Eye, "toggle-watch-play"], ["job", "Show My Job", User, "toggle-show-my-job"], ["walk", "Walk-Through", Footprints, "toggle-walkthrough"], ...(play.quiz.length ? [["quiz", "Quiz", ListChecks, "toggle-quiz"]] : [])];
  const canvasClass = full ? "max-h-[70vh] w-auto mx-auto h-auto" : "w-full h-auto";

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <Link to={viewer ? "/view" : `/teams/${play.team_id}/playbook`} className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-white mb-1" data-testid="learn-back-playbook"><ArrowLeft size={16} />Playbook</Link>
          <p className="label mb-0">{catLabel(play.category)}{play.formation && ` · ${play.formation}`}</p>
          <h1 className="h1" data-testid="learn-play-name">{play.name}</h1>
          {play.is_example && <p className="text-xs text-[#FACC15] mt-1" data-testid="learn-example-note">Example play — coaches should adapt it to their league and team.</p>}
        </div>
        {!viewer && (
          <div className="flex gap-2">
            <Link to={`/teams/${play.team_id}/print?plays=${play.id}`} className="btn btn-ghost" data-testid="learn-print-link"><Printer size={16} />Print</Link>
            <Link to={`/play/${play.id}/edit`} className="btn btn-ghost" data-testid="learn-edit-link"><Pencil size={16} />Edit play</Link>
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-2" role="tablist" data-testid="learn-modes">
        {MODES.map(([k, l, Icon, tid]) => <button key={k} role="tab" aria-selected={mode === k} className={`btn ${mode === k ? "btn-primary" : "btn-ghost"}`} onClick={() => go(k)} data-testid={tid}><Icon size={18} />{l}</button>)}
        {mode === "job" && <button className="btn btn-ghost" onClick={replay} data-testid="btn-replay-my-assignment-top">Replay My Assignment</button>}
      </div>
      {mode !== "watch" && <PositionPicker play={play} posId={posId} setPosId={(id) => { setPosId(id); pb.seek(0); }} />}

      {mode === "walk" ? <WalkThrough key={posId || "none"} play={play} team={team} pb={pb} posId={posId} canvasClass="w-full h-auto" /> :
       mode === "quiz" ? <QuizRunner key={posId || "none"} play={play} team={team} posId={posId} onReplay={replay} /> : (
        <div className="grid lg:grid-cols-[1fr_380px] gap-4">
          <div className={full ? "fixed inset-0 z-[60] bg-[#070D18] p-3 flex flex-col justify-center gap-3 overflow-auto" : "space-y-3"} data-testid="learn-stage">
            <div className="rounded-2xl overflow-hidden border border-[#1F3358]">
              <PlayCanvas play={play} team={team} t={pb.t} showRoutes={trails || mode === "job"} highlightId={mode === "job" ? posId : null} className={canvasClass} />
            </div>
            <div className="card p-3"><PlaybackBar play={play} pb={pb} trails={trails} setTrails={setTrails} full={full} setFull={setFull} /></div>
          </div>
          <div className="space-y-3">
            {mode === "job" && player ? <JobCard play={play} player={player} onReplay={replay} /> : mode === "job" ? <p className="card p-5 text-slate-300" data-testid="job-pick-prompt">Pick your position above to see your job.</p> : (
              <div className="card p-4 text-sm text-slate-300 space-y-2"><p className="h2">Watch the whole play</p><p>Press Play. Use the speed buttons to slow it down. Then tap <b>Show My Job</b>.</p>{play.description && !play.is_example && <p className="text-slate-400">{play.description}</p>}</div>
            )}
            <div className="card p-4"><Legend /><p className="text-xs text-slate-500 mt-3">This is a teaching diagram of the coach's plan, not a prediction of what defenders will do.</p></div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function LearnPlay({ viewer = false }) {
  const { playId } = useParams();
  const { play, team, err } = useLearnData(playId, viewer);
  const body = err ? (
    <div className="card p-8 text-center max-w-md mx-auto" data-testid="learn-error"><p className="h2">Can't open this play</p><p className="text-slate-400 mt-2">{err}</p>
      <Link to={viewer ? "/join" : "/teams"} className="btn btn-primary mt-5" data-testid="learn-error-back">{viewer ? "Enter a team code" : "Back to teams"}</Link></div>
  ) : !play ? <p className="text-slate-400" data-testid="learn-loading">Loading play…</p> : <LearnBody play={play} team={team} viewer={viewer} />;
  if (!viewer) return body;
  return (
    <div className="min-h-screen bg-[#070D18] text-white pb-10">
      <ViewerHeader team={team} />
      <main className="max-w-7xl mx-auto px-3 sm:px-6 py-5">{body}</main>
    </div>
  );
}
